import { useContext, useEffect } from "react";
import { Box } from "@mui/material";
import ItemsList from "./ItemsList";
import TransactionListTableHead from "./TransactionListTableHeader";
import { useAllTransactionsList } from "../../../../hooks/useTransactionsList";
import { FilterNonIdeal, TitleAndActionBox } from "../../../common";
import { LanguageContext } from "../../../../context/LanguageContext";
import { AppPagination, UBButton } from "../../../../Libraries";
import withObservables from "@nozbe/with-observables";
import { of } from "rxjs";
import styles from "./styles.module.css";
import {
  DEFAULT_DATE_FILTERS_EXPORT,
  ITEMS_PER_PAGE,
  TABLE_FILTER_BUSINESS_OVERVIEW,
} from "../../../../constants";
import AppTableFilters from "../../../common/AppTableFilters";
import { buildFiltersConfig } from "../../../../utils";
import { useFiltersHandler } from "../../../../hooks/useFilterHandler";
import useDownlaodPdf from "../../../../hooks/useDownlaodPdf";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { useLocation, useNavigate } from "react-router-dom";
import { navigateToEdit } from "../../../Features/NewInvoiceBook/invoiceBookUtils";

const BusinessOverviewTransactionsList = ({
  accountWrapperClassName,
  accountListBodyWarpperClassName,
  business,
  initialAllTransactions,
  isShowAll,
  hideTitle = false,
  onLoadingChange,
  user,
} = {}) => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    filters,
    appliedChips,
    handleFilterChange,
    resetFilters,
    handleRemoveChip,
    isResetFilter,
    setIsResetFilter,
    isAppliedFilter,
  } = useFiltersHandler(undefined, I18n);

  const { onDownload, downloadLoading } = useDownlaodPdf(user);

  const {
    transactionsList,
    handleAllTransactions,
    totalTransactionsFilterCount,
    isLoading,
  } = useAllTransactionsList(
    initialAllTransactions,
    isShowAll ? ITEMS_PER_PAGE : 10,
  );

  const initialGetData = () => {
    handleAllTransactions({
      business,
      filters: filters,
      offset: 0,
      limit: isShowAll ? ITEMS_PER_PAGE : 10,
    });
  };

  useEffect(() => {
    initialGetData();
  }, [filters]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading, onLoadingChange]);

  const handleDownloadReport = (startDate, endDate) => {
    triggerLogEvent(analytics, "business_transaction_report_download");
    let paramsForReport = {
      name: "invoice_statement.pdf",
      template_name: "accountreport_new.hbs",
      business_id: business.id,
      start_date: startDate || "",
      end_date: endDate || "",
      type: "expense",
      invoice_type: "sales",
      pdf: true,
    };

    onDownload(paramsForReport, () => {}, navigate);
  };

  const handleEdit = (item) => {
    triggerLogEvent(analytics, "quick_sale_edit");
    let tranType = item?.tranType?.toLowerCase() || "";
    if (tranType == "bill") {
      navigateToEdit(
        navigate,
        item?.type,
        {
          invoiceId: item.id,
          customerId: item?.customer?.id || "",
        },
        location,
      );
    } else {
      if (item?.type == "in") {
        navigate(`/QuickSale/QuickSaleForm/Edit/${item.id}`, {
          state: {
            pathname: location.pathname,
          },
        });
      } else {
        triggerLogEvent(analytics, "exp_edit");
        navigate(`/Expense/ExpenseForm/Edit/${item.id}`, {
          state: {
            pathname: location.pathname,
          },
        });
      }
    }
  };

  return (
    <Box className={accountWrapperClassName || "AccountWrapper"}>
      {hideTitle || isShowAll ? null : (
        <Box className={styles.titleContainer}>
          <TitleAndActionBox
            data={{
              title: I18n.transactions,
              className: styles.titleWrapper,
            }}
          >
            <UBButton
              onClick={() => navigate("/BusinessOverviewTransactions")}
              id={"transactions_view_all"}
              className={`${styles.viewAllBtn}`}
              title={I18n.view_all}
            />
          </TitleAndActionBox>
        </Box>
      )}

      {isShowAll ? (
        <AppTableFilters
          filters={TABLE_FILTER_BUSINESS_OVERVIEW}
          onFilterChange={handleFilterChange}
          appliedChips={appliedChips}
          filterState={filters}
          onRemoveChip={(chip) => handleRemoveChip(chip)}
          filtersConfig={buildFiltersConfig(TABLE_FILTER_BUSINESS_OVERVIEW, [], "", [
              { label: I18n.sale, value: "sale" },
              { label: I18n.expense, value: "expense" },
            ], I18n.search_by_reference_number, null, null, null, null, I18n)}
          isResetFilter={isResetFilter}
          setIsResetFilter={setIsResetFilter}
          // exportConfig={{
          //   onExport: handleDownloadReport,
          //   loading: downloadLoading,
          //   dateFilters: DEFAULT_DATE_FILTERS_EXPORT,
          //   applyBtnText: I18n.export,
          //   spinnerColor: "black",
          // }}
        />
      ) : null}

      <TransactionListTableHead
        filters={filters}
        handleFilterChange={handleFilterChange}
        isShowAll={isShowAll}
        disabled={!transactionsList.length}
      />

      <Box
        className={`${
          accountListBodyWarpperClassName || "accountListBodyWarpper"
        } generic-scrollbar`}
        id="scrollableDiv"
      >
        {transactionsList.length > 0 ? (
          isShowAll ? (
            <AppPagination
              data={{
                handleNext: () => {
                  if (transactionsList.length < totalTransactionsFilterCount)
                    handleAllTransactions({
                      business,
                      offset: transactionsList.length,
                      limit: ITEMS_PER_PAGE,
                      filters,
                      isCalculateTotal: false,
                    });
                },
                pageLoad: true,
                count: totalTransactionsFilterCount,
                list: transactionsList,
                scrollableTarget: "scrollableDiv",
              }}
            >
              {transactionsList.map((item, index) => (
                <ItemsList
                  key={index}
                  index={index}
                  sale={item}
                  business={business}
                  refetchList={initialGetData}
                  onEdit={() => {
                    handleEdit(item);
                  }}
                />
              ))}
            </AppPagination>
          ) : (
            transactionsList.map((item, index) => (
              <ItemsList
                key={index}
                index={index}
                sale={item}
                business={business}
                refetchList={initialGetData}
                onEdit={() => {
                  handleEdit(item);
                }}
              />
            ))
          )
        ) : isAppliedFilter && !isLoading ? (
          <Box className="h-100">
            <FilterNonIdeal
              title={I18n.no_transactions_matching_those_filters}
              subTitle={I18n.try_adjusting_filters}
              subTitleTextOnClick={() => resetFilters()}
              subTitleText={I18n.reset_small}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

const enhance = withObservables(["business"], ({ business }) => {
  return {
    customers: business ? business?.getAllCustomers.observe() : of([]),
    initialAllTransactions: business
      ? business.getAllRecentTransactions(undefined, undefined, "").observe()
      : of([]),
  };
});

export default enhance(BusinessOverviewTransactionsList);
